// src/App.js
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './App.css';
import { LoadScript } from '@react-google-maps/api';
import LocationPicker from './components/LocationPicker';




// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Card Component
const Card = ({ title, children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
    {title && <h3 className="text-xl font-semibold mb-4 text-gray-800">{title}</h3>}
    <div className="space-y-4">
      {children}
    </div>
  </div>
);

// Button Component
const Button = ({ children, onClick, disabled = false, variant = 'primary', className = '' }) => {
  const baseStyles = 'px-4 py-2 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-100 text-gray-800 hover:bg-gray-200 focus:ring-gray-300',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

// Input Component
const Input = ({ label, name, value, onChange, type = 'text', className = '', ...props }) => (
  <div className={`mb-4 ${className}`}>
    {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <input
      type={type}
      name={name}
      id={name}
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      {...props}
    />
  </div>
);

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Error Message Component
const ErrorMessage = ({ message }) => (
  message && (
    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">{message}</p>
        </div>
      </div>
    </div>
  )
);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function App() {
  const _today = new Date();

  const _convertTemps = (tempF) => {
    if (tempF === undefined || tempF === null) return null;
    return displayUnit === 'F' ? tempF : (tempF - 32) * 5/9;
  };
  
  const [displayUnit, setDisplayUnit] = useState('F'); // 'F' or 'C'
  
  const [formData, setFormData] = useState({
    poolLength: 30,
    poolWidth: 15,
    poolDepth: 4.5,
    poolVolume: 20000,
    poolLocation: '',
    latitude: '',
    longitude: '',
    locationType: 'coords',
    targetTemperatureF: '83',
    poolOpenDate: new Date(new Date().getFullYear(), 5, 15), // June 15
    poolCloseDate: new Date(new Date().getFullYear(), 8, 15), // September 15
    //useSolarCover: false,
    poolCover: 'none',
    poolSurfaceArea: '',
    initialPoolTempF: '60',
    simulationYears: '3',
    heaterStartHour: '20',
    heaterEndHour: '8',
    
    // Heater 1 (primary heater)
    heater1Type: 'Electric (heat pump)',
    heater1BTU: '140000',
    heater1Cop: '5.0',
    heater1ElectricityCost: '0.15',
    heater1PropaneCost: '2.50',
    heater1MethaneCost: '1.50',
    heater1CopReferenceTemp: '80',
    heater1CopTempSensitivity: '0.05',
    heater1MinCop: '2.0',
    
    // Heater 2 (comparison, initially hidden)
    showSecondHeater: false,
    heater2Type: 'Electric (heat pump)',
    heater2BTU: '140000',
    heater2Cop: '5.0',
    heater2ElectricityCost: '0.15',
    heater2PropaneCost: '2.50',
    heater2MethaneCost: '1.50',
    heater2CopReferenceTemp: '80',
    heater2CopTempSensitivity: '0.05',
    heater2MinCop: '2.0',
  });
  
  // Convert temperature between F and C
  const convertTemp = (temp, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return temp;
    if (fromUnit === 'F' && toUnit === 'C') return (temp - 32) * 5/9;
    if (fromUnit === 'C' && toUnit === 'F') return (temp * 9/5) + 32;
    return temp;
  };
  
  // Get display temperature (converts from stored F to display unit)
  const getDisplayTemp = (tempF) => {
    return displayUnit === 'F' ? tempF : convertTemp(tempF, 'F', 'C');
  };
  
  // Convert input temperature (from display unit to stored F)
  const parseInputTemp = (input, currentValueF) => {
    const num = parseFloat(input);
    if (isNaN(num)) return currentValueF;
    return displayUnit === 'F' ? num : convertTemp(num, 'C', 'F');
  };
  
  // Format temperature for display
  const _formatTemp = (tempF) => {
    const value = getDisplayTemp(tempF);
    return displayUnit === 'F' ? `${Math.round(value)}°F` : `${value.toFixed(1)}°C`;
  };
  
  // Calculate electrical draw in kW based on BTU and COP
  const calculateElectricalDraw = (btu, cop) => {
    // Convert BTU/hr to kW: 1 kW = 3412.14 BTU/hr
    // Then divide by COP to get electrical input power
    return (btu / 3412.14) / (cop || 1);
  };

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [isLocating, setIsLocating] = useState(false);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
        setIsLocating(false);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access was denied. Please enable location services and try again.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'The request to get your location timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        setLocationError(errorMessage);
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const downloadHourlyData = () => {
    if (!results?.hourlyData?.length) return;
    
    const hasComparison =
    Array.isArray(results.hourlyData2) && results.hourlyData2.length;

  /***** Step 1 – merge & prefix *****/
  let combinedData = results.hourlyData;

  if (hasComparison) {
    // Build a quick lookup keyed by datetime for robustness
    const comparisonLookup = Object.fromEntries(
      results.hourlyData2.map((d) => [d.datetime, d])
    );

    combinedData = results.hourlyData.map((row) => {
      const comp = comparisonLookup[row.datetime];
      if (!comp) return row; // safeguard

      // prefix every key in the comparison object
      const prefixed = Object.fromEntries(
        Object.entries(comp).map(([k, v]) => [`comparison_${k}`, v])
      );

      return { ...row, ...prefixed };
    });
  }

  /***** Step 2 – headers *****/
  const headers = hasComparison
    ? [
        "Date & Time",
        `Air Temp (${displayUnit})`,
        `Pool Temp (No Heater) (${displayUnit})`,
        `Target Temp (${displayUnit})`,

        /* primary */
        `Primary: Pool Temp (${displayUnit})`,
        "Primary: Heater On",
        "Primary: Heater Energy (kWh)",
        "Primary: Heater Energy (Gallons)",
        "Primary: Heater Energy (Therms)",

        /* comparison */
        `Comparison: Pool Temp (${displayUnit})`,
        "Comparison: Heater On",
        "Comparison: Heater Energy (kWh)",
        "Comparison: Heater Energy (Gallons)",
        "Comparison: Heater Energy (Therms)",

        /* met data */
        "Solar Irradiance (W/m²)",
        "Wind Speed (mph)",
        "Relative Humidity (%)",
      ]
    : [
        "Date & Time",
        `Air Temp (${displayUnit})`,
        `Pool Temp (No Heater) (${displayUnit})`,
        `Target Temp (${displayUnit})`,
        `Pool Temp (${displayUnit})`,
        "Heater On",
        "Heater Energy (kWh)",
        "Heater Energy (Gallons)",
        "Heater Energy (Therms)",
        "Solar Irradiance (W/m²)",
        "Wind Speed (mph)",
        "Relative Humidity (%)",
      ];

  /***** Step 3 – rows *****/
  const rows = combinedData.map((item) =>
    hasComparison
      ? [
          `"${item.datetime}"`,
          item.air_temp_f,
          item.pool_temp_no_heater_f,
          item.target_temp_f,

          /* primary */
          item.pool_temp_f,
          item.heater_on ? "Yes" : "No",
          item.heater_kwh,
          item.heater_gallons,
          item.heater_therms,

          /* comparison */
          item.comparison_pool_temp_f,
          item.comparison_heater_on ? "Yes" : "No",
          item.comparison_heater_kwh,
          item.comparison_heater_gal,
          item.comparison_heater_therms,

          /* met data */
          item.solar_irradiance_W_m2 ?? 0,
          item.wind_speed_mph,
          item.relative_humidity,
        ]
      : [
          `"${item.datetime}"`,
          item.air_temp_f,
          item.pool_temp_no_heater_f,
          item.target_temp_f,
          item.pool_temp_f,
          item.heater_on ? "Yes" : "No",
          item.heater_kwh,
          item.heater_gal,
          item.heater_therms,
          item.solar_irradiance_W_m2 ?? 0,
          item.wind_speed_mph,
          item.relative_humidity,
        ]
  );
    
    // Combine header and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pool_simulation_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const [activeTab, setActiveTab] = useState('input');

  const toggleSecondHeater = () => {
    setFormData(prev => ({
      ...prev,
      showSecondHeater: !prev.showSecondHeater
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      // Handle heater type changes
      if (name === 'heater1Type' || name === 'heater2Type') {
        const heaterPrefix = name === 'heater1Type' ? 'heater1Cop' : 'heater2Cop';
        
        if (value === 'Electric (resistance)') {
          // Set COP to 1 for resistance heaters
          return {
            ...prev,
            [name]: value,
            [heaterPrefix]: '1.0'
          };
        } else if (value === 'Electric (heat pump)') {
          // Set COP to 5 for heat pumps
          return {
            ...prev,
            [name]: value,
            [heaterPrefix]: '5.0'
          };
        }
      }
      
      // For all other inputs, update the field normally
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : 
                (name === 'targetTemperatureF' ? parseInputTemp(value, prev.targetTemperatureF) : value),
      };
    });
  };

  const handleDateChange = (date, name) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Parse numeric values with proper error handling
    const parseNumeric = (value, defaultValue = 0) => {
      const num = parseFloat(value);
      return isNaN(num) ? defaultValue : num;
    };

    // Prepare the base request data
    const requestData = {
      pool_volume_gallons: parseNumeric(formData.poolVolume, 20000),
      pool_open_date: formData.poolOpenDate.toISOString().split('T')[0],
      pool_close_date: formData.poolCloseDate.toISOString().split('T')[0],
      heater_operating_start_hour: parseInt(formData.heaterStartHour, 10),
      heater_operating_end_hour: parseInt(formData.heaterEndHour, 10),
      target_temp: parseNumeric(formData.targetTemperatureF, 82),
      target_temp_unit: formData.temperatureUnit || 'F',
      use_solar_cover: formData.poolCover !== 'none',
      
      heater_type: formData.heater1Type,
      electricity_cost: parseNumeric(formData.heater1ElectricityCost, 0.15),
      propane_cost: parseNumeric(formData.heater1PropaneCost, 2.50),
      methane_cost: parseNumeric(formData.heater1MethaneCost, 1.00),
      heater_btu: parseNumeric(formData.heater1BTU, 118000),  // Add BTU info for reference
      heater_cop: parseNumeric(formData.heater1Cop, 1), // Relevant for heat pumps 

      show_second_heater: formData.showSecondHeater,
      heater_type_2: formData.heater2Type,
      electricity_cost_2: parseNumeric(formData.heater2ElectricityCost, 0.15),
      propane_cost_2: parseNumeric(formData.heater2PropaneCost, 2.50),
      methane_cost_2: parseNumeric(formData.heater2MethaneCost, 1.00),
      heater_btu_2: parseNumeric(formData.heater2BTU, 118000),  // Add BTU info for reference
      heater_cop_2: parseNumeric(formData.heater2Cop, 1), // Relevant for heat pumps 
    };

    // Add location data
    requestData.latitude = formData.latitude;
    requestData.longitude = formData.longitude;

    console.log('Sending request with data:', JSON.stringify(requestData, null, 2));

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/simulate`, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('Received response:', JSON.stringify(response.data, null, 2));
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Process the response to ensure all expected fields exist
      const responseData = response.data;
      
      const processedResults = {
        // Original response data
        ...responseData,
        
        // Ensure all expected fields have values
        pool_open_date: responseData.pool_open_date || requestData.pool_open_date,
        pool_close_date: responseData.pool_close_date || requestData.pool_close_date,
        
        // Handle different possible field names for cost and energy
        total_cost: responseData.total_cost || responseData.total_annual_cost || 0,
        total_kwh: responseData.total_kwh || responseData.total_annual_kwh || 0,
        
        // Ensure monthly_breakdown exists
        monthly_breakdown: responseData.monthly_breakdown || {}
      };
      
      console.log('Processed results:', JSON.stringify(processedResults, null, 2));
      
      setResults(processedResults);
      setActiveTab('results');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'An error occurred while simulating');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResults(null);
    setError('');
    setActiveTab('input');
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          usePointStyle: true,
        }
      },
      title: { 
        display: true, 
        text: `Average Daily Pool & Air Temperatures (3-Year Avg) in °${displayUnit}` 
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label = label.split(' (')[0]; // Remove unit from label in tooltip
              label += `: ${displayUnit === 'F' ? Math.round(context.parsed.y) : context.parsed.y.toFixed(1)}°${displayUnit}`;
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        title: { 
          display: true, 
          text: `Temperature (°${displayUnit})` 
        },
        ticks: {
          callback: function(value) {
            return displayUnit === 'F' ? Math.round(value) : value.toFixed(1);
          }
        }
      },
      x: {
        title: { 
          display: true, 
          text: 'Day of Season (MM-DD)'
        }
      }
    }
  }), [displayUnit]);

  const chartData = useMemo(() => {
    if (!results?.chartData) return { labels: [], datasets: [] }; // Return empty chart data if results are not available

    const chartData = results.chartData;
    
    // Convert temperature data based on display unit
    const convertTemps = (tempF) => {
      if (tempF === undefined || tempF === null) return null;
      return displayUnit === 'F' ? tempF : (tempF - 32) * 5/9;
    };

    let comparison_avg_temp_with_heater_f = null;
    if (results?.chartData2?.avg_temp_with_heater_f?.length > 0) {
      comparison_avg_temp_with_heater_f = {
        label: `Comparison Pool Temp w/ Heater (°${displayUnit})`,
        data: (results.chartData2.avg_temp_with_heater_f || []).map(convertTemps),
        borderColor: 'rgb(217, 38, 214)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        tension: 0.2,
        fill: false,
        pointRadius: 0
      }
    } // Check if comparison data is available, if so add it to the chart


    // Create an array with the target temperature repeated for each data point
    const targetTempArray = Array(chartData.avg_temp_with_heater_f?.length || 0).fill(convertTemps(formData.targetTemperatureF));
    
    let target_temp_dataset = {
      label: `Target Temperature (°${displayUnit})`,
      data: targetTempArray,
      borderColor: 'rgb(0, 0, 0)',
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [5, 5],
      tension: 0,
      fill: false,
      pointRadius: 0,
      borderCapStyle: 'round',
      borderJoinStyle: 'round'
    }
    
    
    return {
      labels: chartData.dates || [],
      datasets: [
        target_temp_dataset,
        {
          label: `Pool Temp w/ Heater (°${displayUnit})`,
          data: (chartData.avg_temp_with_heater_f || []).map(convertTemps),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          tension: 0.2,
          fill: false,
          pointRadius: 0
        },
        {
          label: `Pool Temp w/o Heater (°${displayUnit})`,
          data: (chartData.avg_temp_without_heater_f || []).map(convertTemps),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          tension: 0.2,
          fill: false,
          pointRadius: 0
        },
        {
          label: `Air Temperature (°${displayUnit})`,
          data: (chartData.avg_air_temp_f || []).map(convertTemps),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 1,
          tension: 0.1,
          fill: false,
          pointRadius: 0
        },
        
        ...(comparison_avg_temp_with_heater_f ? [comparison_avg_temp_with_heater_f] : [])
      ]
    };
  }, [results, displayUnit]);

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={['places', 'marker']}        // <-- required for Autocomplete
      
    >
    <div className="min-h-screen bggray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <img 
              src="/logo_full.png" 
              alt="AquaCost" 
              className="h-16 w-auto" 
            />
          </div>
          <p className="mt-2 text-lg text-gray-600">Calculate your pool heating costs with precision</p>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex space-x-4 mb-6">
              <Button
                onClick={() => setActiveTab('input')}
                variant={activeTab === 'input' ? 'primary' : 'secondary'}
                className="px-6"
              >
                Simulation Parameters
              </Button>
              <Button
                onClick={() => setActiveTab('how-it-works')}
                variant={activeTab === 'how-it-works' ? 'primary' : 'secondary'}
                className="px-6"
              >
                How it Works
              </Button>
              <Button
                onClick={() => setActiveTab('results')}
                variant={activeTab === 'results' ? 'primary' : 'secondary'}
                disabled={!results}
                className="px-6"
              >
                Results
              </Button>
            </div>

            {error && <ErrorMessage message={error} />}

            {loading ? (
              <div className="py-12">
                <LoadingSpinner />
                <p className="text-center text-gray-500 mt-4">Calculating your pool heating costs...</p>
              </div>
            ) : activeTab === 'input' ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card title="Pool Details">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="Average Depth (ft)"
                        name="poolDepth"
                        type="number"
                        value={formData.poolDepth}
                        onChange={handleInputChange}
                        min="0"
                        step="0.1"
                        required
                      />
                      <Input
                        label="Volume (gallons)"
                        name="poolVolume"
                        type="number"
                        value={formData.poolVolume}
                        onChange={handleInputChange}
                        min="0"
                        step="100"
                        required
                      />
                    </div>
                  </Card>
                  <Card title="Season Dates">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pool Open Date</label>
                        <DatePicker
                          selected={formData.poolOpenDate}
                          onChange={(date) => handleDateChange(date, 'poolOpenDate')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          dateFormat="MMMM d, yyyy"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pool Close Date</label>
                        <DatePicker
                          selected={formData.poolCloseDate}
                          onChange={(date) => handleDateChange(date, 'poolCloseDate')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          dateFormat="MMMM d, yyyy"
                          required
                        />
                      </div>
                    </div>
                  </Card>

                  <Card title="Heating Settings">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-sm font-medium text-gray-700">Target Temperature</label>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => setDisplayUnit('F')}
                                className={`text-sm px-2 py-1 rounded ${displayUnit === 'F' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                              >
                                °F
                              </button>
                              <button
                                type="button"
                                onClick={() => setDisplayUnit('C')}
                                className={`text-sm px-2 py-1 rounded ${displayUnit === 'C' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                              >
                                °C
                              </button>
                            </div>
                          </div>
                          <div className="flex rounded-md shadow-sm">
                            <input
                              type="number"
                              name="targetTemperatureF"
                              value={Math.round(getDisplayTemp(formData.targetTemperatureF))}
                              onChange={handleInputChange}
                              min={displayUnit === 'F' ? 50 : 10}
                              max={displayUnit === 'F' ? 100 : 38}
                              step="1"
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300"
                              required
                            />
                            <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                              °{displayUnit}
                            </span>
                          </div>
                        </div>
                        
                        <div>
                          {/* Placeholder to maintain layout */}
                          <div className="h-10"></div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Hour</label>
                            <select
                              name="heaterStartHour"
                              value={formData.heaterStartHour}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{i}:00</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Hour</label>
                            <select
                              name="heaterEndHour"
                              value={formData.heaterEndHour}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{i}:00</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <div>
                          <label htmlFor="useSolarCover" className="block text-sm font-medium text-gray-700">
                            Use Solar Cover
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            name="poolCover"
                            id="useSolarCover"
                            checked={formData.poolCover === 'solar'}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              poolCover: e.target.checked ? 'solar' : 'none'
                            }))}
                            className="toggle-checkbox"
                          />
                          <label
                            htmlFor="useSolarCover"
                            className="toggle-label"
                          />
                        </div>
                      </div>
                      {/* Heater 1 */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Heater 1 (Primary)</h3>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.heater1Type.includes('Gas') ? 'Gas Cost' : 'Electricity Cost'}
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              name={
                                formData.heater1Type === 'Gas (propane)' ? 'heater1PropaneCost' :
                                formData.heater1Type === 'Gas (methane)' ? 'heater1MethaneCost' :
                                'heater1ElectricityCost'
                              }
                              value={
                                formData.heater1Type === 'Gas (propane)' ? formData.heater1PropaneCost :
                                formData.heater1Type === 'Gas (methane)' ? formData.heater1MethaneCost :
                                formData.heater1ElectricityCost
                              }
                              onChange={handleInputChange}
                              min="0"
                              step="0.01"
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                              placeholder="0.00"
                              required
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">
                                {formData.heater1Type === 'Gas (propane)' ? '/gallon' : 
                                 formData.heater1Type === 'Gas (methane)' ? '/therm' : 
                                 '/kWh'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Heater Type</label>
                          <select
                            name="heater1Type"
                            value={formData.heater1Type}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="Electric (heat pump)">Electric (heat pump)</option>
                            <option value="Gas (propane)">Gas (propane)</option>
                            <option value="Gas (methane)">Gas (methane)</option>
                            <option value="Electric (resistance)">Electric (resistance)</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (BTU/hr)</label>
                            <input
                              type="number"
                              name="heater1BTU"
                              step="1000"
                              min="10000"
                              max="400000"
                              value={formData.heater1BTU}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          {formData.heater1Type === 'Electric (heat pump)' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Efficiency (COP)</label>
                                <input
                                  type="number"
                                  name="heater1Cop"
                                  step="0.1"
                                  min="1"
                                  max="10"
                                  value={formData.heater1Cop}
                                  onChange={handleInputChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            )}
                            {formData.heater1Type === 'Electric (heat pump)' && (
                              <div className="bg-white p-3 rounded-md border border-gray-200">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Electrical Draw</label>
                                <div className="text-lg font-semibold">
                                  {calculateElectricalDraw(parseFloat(formData.heater1BTU || 0), parseFloat(formData.heater1Cop || 1)).toFixed(2)} <span className="text-sm font-normal text-gray-500">kW</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  ({formData.heater1BTU} BTU/hr ÷ {formData.heater1Cop} COP)
                                </div>
                              </div>
                            )}

                        </div>
                      </div>

                      {/* Heater 2 - Conditionally Rendered */}
                      {formData.showSecondHeater && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Heater 2 (Comparison)</h3>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, showSecondHeater: false }))}
                              className="text-gray-400 hover:text-gray-500"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Heater Type */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Heater Type</label>
                            <select
                              name="heater2Type"
                              value={formData.heater2Type}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="Electric (heat pump)">Electric (heat pump)</option>
                              <option value="Gas (propane)">Gas (propane)</option>
                              <option value="Gas (methane)">Gas (methane)</option>
                              <option value="Electric (resistance)">Electric (resistance)</option>
                            </select>
                          </div>

                          {/* Cost Input */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {formData.heater2Type.includes('Gas') ? 'Gas Cost' : 'Electricity Cost'}
                            </label>
                            <div className="relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">$</span>
                              </div>
                              <input
                                type="number"
                                name={
                                  formData.heater2Type === 'Gas (propane)' ? 'heater2PropaneCost' :
                                  formData.heater2Type === 'Gas (methane)' ? 'heater2MethaneCost' :
                                  'heater2ElectricityCost'
                                }
                                value={
                                  formData.heater2Type === 'Gas (propane)' ? formData.heater2PropaneCost :
                                  formData.heater2Type === 'Gas (methane)' ? formData.heater2MethaneCost :
                                  formData.heater2ElectricityCost
                                }
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                                placeholder="0.00"
                                required
                              />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">
                                  {formData.heater2Type === 'Gas (propane)' ? '/gallon' : 
                                   formData.heater2Type === 'Gas (methane)' ? '/therm' : 
                                   '/kWh'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (BTU/hr)</label>
                              <input
                                type="number"
                                name="heater2BTU"
                                step="1000"
                                min="10000"
                                max="400000"
                                value={formData.heater2BTU}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            {formData.heater2Type === 'Electric (heat pump)' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Efficiency (COP)</label>
                              <input
                                type="number"
                                name="heater2Cop"
                                step="0.1"
                                min="1"
                                max="10"
                                value={formData.heater2Cop}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            )}
                            {formData.heater2Type === 'Electric (heat pump)' && (
                              <div className="bg-white p-3 rounded-md border border-gray-200">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Electrical Draw</label>
                                <div className="text-lg font-semibold">
                                  {calculateElectricalDraw(parseFloat(formData.heater2BTU || 0), parseFloat(formData.heater2Cop || 1)).toFixed(2)} <span className="text-sm font-normal text-gray-500">kW</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  ({formData.heater2BTU} BTU/hr ÷ {formData.heater2Cop} COP)
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Add Heater Button */}
                      {!formData.showSecondHeater && (
                        <button
                          type="button"
                          onClick={toggleSecondHeater}
                          className="mt-2 w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="-ml-1 mr-2 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Add Second Heating Option for Comparison
                        </button>
                      )}
                    </div>
                  </Card>

                  <Card title="Location & Environment">
                    <div className="space-y-4">
                      <div className="space-y-2">
                      <div className="pt-2">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleUseCurrentLocation}
                          className="w-full flex items-center justify-center"
                          disabled={isLocating}
                        >
                          {isLocating ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Locating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              Use My Current Location
                            </>
                          )}
                        </Button>
                        {locationError && (
                          <p className="mt-2 text-sm text-red-600">{locationError}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Latitude"
                          name="latitude"
                          type="number"
                          step="0.000001"
                          value={formData.latitude}
                          onChange={handleInputChange}
                          placeholder="e.g. 40.7128"
                          required
                        />
                        <Input
                          label="Longitude"
                          name="longitude"
                          type="number"
                          step="0.000001"
                          value={formData.longitude}
                          onChange={handleInputChange}
                          placeholder="e.g. -74.0060"
                          required
                        />
                      </div>
                      
                      <LocationPicker
                        lat={Number(formData.latitude) || null}
                        lng={Number(formData.longitude) || null}
                        onChange={({ lat, lng }) =>
                          setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))
                        }
                      />

                    </div>
                  </div>
                </Card>
                </div>


                <div className="flex justify-end space-x-4 pt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                    className="px-8"
                  >
                    Calculate
                  </Button>
                </div>
              </form>
            ) : activeTab === 'how-it-works' ? (
              <div className="space-y-6 w-full">
                <Card title="How AquaCost Works">
                  <div className="prose max-w-none">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Understanding Your Pool Heating Costs</h3>
                    
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">1. Data Collection</h4>
                        <p>We gather detailed weather data for your location, including:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Hourly temperature and humidity</li>
                          <li>Wind speed and direction</li>
                          <li>Solar radiation and cloud cover</li>
                          <li>Historical weather patterns</li>
                        </ul>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-2">2. Heat Loss Calculation</h4>
                        <p>Our model calculates heat loss through multiple mechanisms:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li><strong>Evaporation:</strong> Based on wind speed, humidity, and pool surface area</li>
                          <li><strong>Convection:</strong> Heat transfer to moving air</li>
                          <li><strong>Radiation:</strong> Heat loss to the night sky</li>
                          <li><strong>Conduction:</strong> Heat transfer through pool walls and floor</li>
                        </ul>
                      </div>


                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-medium text-yellow-800 mb-2">3. Heater Simulation</h4>
                        <p>We simulate your heater's operation based on:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Your specified target temperature</li>
                          <li>Heater capacity (BTU rating)</li>
                          <li>Heating efficiency (COP)</li>
                          <li>Operating schedule (if specified)</li>
                        </ul>
                      </div>

                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-medium text-purple-800 mb-2">4. Cost Calculation</h4>
                        <p>Finally, we calculate your estimated costs by:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Converting heater runtime to kilowatt-hours (kWh)</li>
                          <li>Applying your local electricity rate</li>
                          <li>Accounting for seasonal variations</li>
                          <li>Providing monthly and annual cost breakdowns</li>
                        </ul>
                      </div>

                      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">Tips for Reducing Heating Costs</h4>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Use a pool cover when the pool is not in use (reduces heat loss by up to 70%)</li>
                          <li>Consider a variable-speed pump to reduce energy consumption</li>
                          <li>Lower your target temperature by just 1-2°F for significant savings</li>
                          <li>Schedule your heater to run during off-peak electricity hours if available in your area</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ) : (
              <div className="space-y-6 w-full">
                <Card title="Simulation Results" className="w-full">
                  <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h2a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">Tip:</span> Try running with and without a solar cover to see the difference in your energy costs and pool temperature.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-96 relative">
                    <Line 
                      data={chartData} 
                      options={{
                        ...chartOptions,
                        maintainAspectRatio: false,
                        responsive: true
                      }} 
                      className="w-full h-full"
                    />
                  </div>
                  
                  {results && (
                    <>
                      <div className="bg-gray-100 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4">Primary Heater: {formData.heater1Type}</h3>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h3 className="font-medium text-blue-800">Average Pool Temperature</h3>
                            <p className="text-2xl font-bold text-blue-600 mt-2">
                              {_convertTemps(results.overallSummary?.avg_seasonal_pool_temp_f)?.toFixed(1) || 'N/A'}°{displayUnit}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              (Without heater: {_convertTemps(results.overallSummary?.avg_seasonal_pool_temp_no_heater_f)?.toFixed(1) || 'N/A'}°{displayUnit})
                            </p>
                          </div>
                          
                          <div className="bg-green-50 p-4 rounded-lg">
                            <h3 className="font-medium text-green-800">Estimated Annual Cost</h3>
                            <p className="text-2xl font-bold text-green-600 mt-2">
                              ${(results.overallSummary?.avg_annual_cost || 0).toLocaleString('en-US', { 
                                minimumFractionDigits: 2, 
                                maximumFractionDigits: 2 
                              })}
                            </p>
                          </div>
                          
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h3 className="font-medium text-purple-800">Annual Energy Usage</h3>
                            {formData.heater1Type.toLowerCase().includes('electric') && (
                              <p className="text-2xl font-bold text-purple-600 mt-2">
                                {(results.overallSummary?.avg_annual_kwh || 0).toLocaleString('en-US', {
                                  maximumFractionDigits: 0
                                })} kWh
                              </p>
                            )}
                            {formData.heater1Type.toLowerCase().includes('propane') && (
                              <p className="text-2xl font-bold text-purple-600 mt-2">
                                {(results.overallSummary?.avg_annual_gallons || 0).toLocaleString('en-US', {
                                  maximumFractionDigits: 0
                                })} gallons
                              </p>
                            )}
                            {formData.heater1Type.toLowerCase().includes('methane') && (
                              <p className="text-2xl font-bold text-purple-600 mt-2">
                                {(results.overallSummary?.avg_annual_therms || 0).toLocaleString('en-US', {
                                  maximumFractionDigits: 0
                                })} therms
                              </p>
                            )}
                            <p className="text-sm text-gray-500 mt-1">
                              (Avg. {Math.round((results.overallSummary?.avg_annual_heater_hours || 0) / 24)} days of heating)
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {results.overallSummary2 && (
                        <div className="mt-8">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-semibold mb-4">Comparison Heater: {formData.heater2Type}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="font-medium text-blue-800">Average Pool Temperature</h3>
                                <p className="text-2xl font-bold text-blue-600 mt-2">
                                  {_convertTemps(results.overallSummary2?.avg_seasonal_pool_temp_f)?.toFixed(1) || 'N/A'}°{displayUnit}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                  (Without heater: {_convertTemps(results.overallSummary2?.avg_seasonal_pool_temp_no_heater_f)?.toFixed(1) || 'N/A'}°{displayUnit})
                                </p>
                              </div>
                              
                              <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="font-medium text-green-800">Estimated Annual Cost</h3>
                                <p className="text-2xl font-bold text-green-600 mt-2">
                                  ${(results.overallSummary2?.avg_annual_cost || 0).toLocaleString('en-US', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                  })}
                                </p>
                              </div>
                              
                              <div className="bg-purple-50 p-4 rounded-lg">
                                <h3 className="font-medium text-purple-800">Annual Energy Usage</h3>
                                {formData.heater2Type.toLowerCase().includes('electric') && (
                              <p className="text-2xl font-bold text-purple-600 mt-2">
                                {(results.overallSummary2?.avg_annual_kwh || 0).toLocaleString('en-US', {
                                  maximumFractionDigits: 0
                                })} kWh
                              </p>
                            )}
                            {formData.heater2Type.toLowerCase().includes('propane') && (
                              <p className="text-2xl font-bold text-purple-600 mt-2">
                                {(results.overallSummary2?.avg_annual_gallons || 0).toLocaleString('en-US', {
                                  maximumFractionDigits: 0
                                })} gallons
                              </p>
                            )}
                            {formData.heater2Type.toLowerCase().includes('methane') && (
                              <p className="text-2xl font-bold text-purple-600 mt-2">
                                {(results.overallSummary2?.avg_annual_therms || 0).toLocaleString('en-US', {
                                  maximumFractionDigits: 0
                                })} therms
                              </p>
                            )}
                                <p className="text-sm text-gray-500 mt-1">
                                  (Avg. {Math.round((results.overallSummary2?.avg_annual_heater_hours || 0) / 24)} days of heating)
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {results.monthlySummary && results.monthlySummary.length > 0 && results.monthlySummary2 && results.monthlySummary2.length > 0 && (
                        <div className="mt-8">
                          <h3 className="text-lg font-semibold mb-4">Monthly Breakdown Comparison</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" rowSpan="2">
                                  Month
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" rowSpan="2">
                                  Avg. Temp (°{displayUnit}) w/o Heater
                                </th>

                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="3">
                                  Primary
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300" colSpan="3">
                                  Comparison
                                </th>
                              </tr>
                              <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" rowSpan="2">
                                  Avg. Temp (°{displayUnit})
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Cost ($)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Energy ({formData.heater1Type.toLowerCase().includes('electric') ? 'kWh' : formData.heater1Type.toLowerCase().includes('propane') ? 'gallons' : 'therms'})
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300" rowSpan="2">
                                  Avg. Temp (°{displayUnit})
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Cost ($)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Energy ({formData.heater2Type.toLowerCase().includes('electric') ? 'kWh' : formData.heater2Type.toLowerCase().includes('propane') ? 'gallons' : 'therms'})
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {results.monthlySummary.map((monthData, i) => (
                                <tr key={monthData.month_name}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {monthData.month_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {_convertTemps(monthData.avg_temp_no_heater_f)?.toFixed(1)}°{displayUnit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {_convertTemps(monthData.avg_temp_f)?.toFixed(1)}°{displayUnit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      ${(monthData.cost || 0).toLocaleString('en-US', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                      })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formData.heater1Type.toLowerCase().includes('electric') ? (results.monthlySummary[i].kwh || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      }) : formData.heater1Type.toLowerCase().includes('propane') ? (results.monthlySummary[i].gallons || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      }) : (results.monthlySummary[i].therms || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-l border-gray-300">
                                      {(results.monthlySummary2[i].avg_temp_f?.toFixed(1) || 0)}°F
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      ${(results.monthlySummary2[i].cost || 0).toLocaleString('en-US', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                      })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {formData.heater2Type.toLowerCase().includes('electric') ? (results.monthlySummary2[i].kwh || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      }) : formData.heater2Type.toLowerCase().includes('propane') ? (results.monthlySummary2[i].gallons || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      }) : (results.monthlySummary2[i].therms || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      })}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      
                      {results.monthlySummary && results.monthlySummary.length > 0 && !results.monthlySummary2 && (
                        <div className="mt-8">
                          <h3 className="text-lg font-semibold mb-4">Monthly Breakdown</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Temp (°{displayUnit})</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Temp w/o Heater (°{displayUnit})</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost ($)</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Energy ({formData.heater1Type.toLowerCase().includes('electric') ? 'kWh' : formData.heater1Type.toLowerCase().includes('propane') ? 'gallons' : 'therms'})</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {results.monthlySummary.map((monthData) => (
                                  <tr key={monthData.month_name}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {monthData.month_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {_convertTemps(monthData.avg_temp_f).toFixed(1)}°{displayUnit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {_convertTemps(monthData.avg_temp_no_heater_f).toFixed(1)}°{displayUnit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      ${(monthData.cost || 0).toLocaleString('en-US', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                      })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formData.heater1Type.toLowerCase().includes('electric') ? (monthData.kwh || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      }) : formData.heater1Type.toLowerCase().includes('propane') ? (monthData.gallons || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      }) : (monthData.therms || 0).toLocaleString('en-US', {
                                        maximumFractionDigits: 0
                                      })}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-8 pt-4 border-t border-gray-200">
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={downloadHourlyData}
                            className="flex items-center"
                            disabled={!results?.hourlyData?.length}
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Simulation Results (CSV)
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="mt-8">
                    <Button
                      onClick={() => setActiveTab('input')}
                      variant="secondary"
                      className="w-full sm:w-auto"
                    >
                      Back to Input
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </LoadScript>
  );

}

export default App;